/**
 * Council admin (`/council`) — docs/08-frontend-and-ux.md §3.11.
 *
 * The seat roster, the grant/vacate actions, and the settings editor that §3.11
 * asks to be a modal with **live impact copy** — "with 12/12, a single rejection
 * or abstention blocks any proposal". That sentence is the whole point of the
 * screen: ADR-0006's consequence is surfaced at the moment of choice rather than
 * discovered later, on a proposal nobody can explain the death of.
 *
 * `bootstrap_mode` is a status banner and never a control. It self-terminates at
 * quorum and the database refuses to reopen it (docs/07 §6.4), so offering a
 * switch would be offering a lie.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { CouncilSeat, SettingsView } from '@famtree/shared';
import { councilApi, settingsApi, usersApi } from '../../api/client.js';
import { problemMessage } from '../../ui/problem.js';
import ui from '../../ui/components.module.css';
import styles from './council.module.css';

export function CouncilPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState(false);

  const roster = useQuery({
    queryKey: ['council', { history: showHistory }],
    queryFn: () => councilApi.seats(showHistory),
  });
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get() });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['council'] });
    await queryClient.invalidateQueries({ queryKey: ['settings'] });
    // A seat change re-tallies every open proposal (docs/07 §5.2).
    await queryClient.invalidateQueries({ queryKey: ['proposals'] });
    await queryClient.invalidateQueries({ queryKey: ['session'] });
  };

  const vacate = useMutation({
    mutationFn: (seatId: string) => councilApi.vacate(seatId),
    onSuccess: refresh,
  });

  if (roster.isPending || settings.isPending) return <p className={ui.muted}>{t('app.loading')}</p>;
  if (roster.isError) return <p className={ui.problem}>{problemMessage(roster.error, t)}</p>;
  if (settings.isError) return <p className={ui.problem}>{problemMessage(settings.error, t)}</p>;

  const view = settings.data.settings;
  const seats = roster.data.data;
  const active = seats.filter((seat) => seat.vacated_at == null);
  const vacantCount = view.council_size - active.length;

  return (
    <div>
      <div className={styles.header}>
        <h1>{t('council.title')}</h1>
        <span className={styles.spacer} />
        <span className={ui.muted}>
          {t('council.settingsSummary', {
            size: view.council_size,
            threshold: view.approval_threshold,
            days: view.voting_window_days,
          })}
        </span>
        <button className={ui.buttonSecondary} type="button" onClick={() => setEditing(true)}>
          {t('council.edit')}
        </button>
      </div>

      {view.bootstrap_mode && (
        <div className={ui.notice} role="status">
          <strong>{t('home.bootstrapTitle')}</strong>
          <p style={{ margin: 'var(--space-2) 0 0' }}>
            {t('admin.bootstrapExplain', {
              granted: view.active_seats,
              threshold: view.approval_threshold,
            })}
          </p>
        </div>
      )}

      {/* §3.11: a council that cannot pass anything says so, in place, with the fix. */}
      {!view.bootstrap_mode && vacantCount > 0 && view.approval_threshold > active.length && (
        <div className={ui.problem} role="status">
          {t('council.deadlocked', {
            vacant: vacantCount,
            threshold: view.approval_threshold,
            seats: active.length,
          })}
        </div>
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">{t('council.seat')}</th>
            <th scope="col">{t('council.holder')}</th>
            <th scope="col">{t('council.since')}</th>
            {showHistory && <th scope="col">{t('council.vacatedAt')}</th>}
            <th scope="col">
              <span className="visually-hidden">{t('council.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows(view, seats, showHistory).map((row) =>
            row.seat ? (
              <SeatRow
                key={row.seat.id}
                seat={row.seat}
                showHistory={showHistory}
                busy={vacate.isPending}
                onVacate={() => vacate.mutate(row.seat!.id)}
              />
            ) : (
              <tr key={`vacant-${row.seatNo}`}>
                <td className="tabular">{row.seatNo}</td>
                <td className={ui.muted}>{t('council.vacant')}</td>
                <td />
                {showHistory && <td />}
                <td>
                  <GrantButton seatNo={row.seatNo} onGranted={refresh} />
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>

      {vacate.isError && <p className={ui.problem}>{problemMessage(vacate.error, t)}</p>}

      <p style={{ marginBlockStart: 'var(--space-4)' }}>
        <button
          className={ui.buttonSecondary}
          type="button"
          onClick={() => setShowHistory((value) => !value)}
        >
          {showHistory ? t('council.hideHistory') : t('council.showHistory')}
        </button>{' '}
        <Link className={ui.buttonSecondary} to="/admin/audit">
          {t('nav.audit')}
        </Link>
      </p>

      {editing && (
        <SettingsEditor
          view={view}
          activeSeats={active.length}
          onClose={() => setEditing(false)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

/**
 * One row per seat number, so a vacancy is a row with a `[Grant…]` rather than a
 * gap — "vacant" is vocabulary, not a stored state (ARCHITECTURE.md §2.1 C2).
 * With history on, the vacated rows are appended as they are: a seat number can
 * have had several holders.
 */
function rows(
  view: SettingsView,
  seats: CouncilSeat[],
  showHistory: boolean,
): Array<{ seatNo: number; seat: CouncilSeat | null }> {
  const active = new Map(seats.filter((s) => s.vacated_at == null).map((s) => [s.seat_no, s]));
  const result: Array<{ seatNo: number; seat: CouncilSeat | null }> = [];
  for (let seatNo = 1; seatNo <= view.council_size; seatNo++) {
    result.push({ seatNo, seat: active.get(seatNo) ?? null });
  }
  if (showHistory) {
    for (const seat of seats.filter((s) => s.vacated_at != null)) {
      result.push({ seatNo: seat.seat_no, seat });
    }
  }
  return result;
}

function SeatRow({
  seat,
  showHistory,
  busy,
  onVacate,
}: {
  seat: CouncilSeat;
  showHistory: boolean;
  busy: boolean;
  onVacate: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const vacated = seat.vacated_at != null;

  return (
    <tr className={vacated ? styles.rowVacated : undefined}>
      <td className="tabular">{seat.seat_no}</td>
      <td>
        {seat.holder_person_id ? (
          <Link to={`/people/${seat.holder_person_id}`}>{seat.holder_display_name}</Link>
        ) : (
          seat.holder_display_name
        )}
      </td>
      <td className="tabular">{formatDate(seat.granted_at)}</td>
      {showHistory && <td className="tabular">{vacated ? formatDate(seat.vacated_at) : ''}</td>}
      <td>
        {!vacated && (
          <button
            className={ui.buttonSecondary}
            type="button"
            disabled={busy}
            onClick={() => {
              // Vacating suppresses the seat's ballots and re-tallies everything
              // open — worth one confirmation (docs/07 §3.4).
              if (window.confirm(t('council.confirmVacate', { name: seat.holder_display_name }))) {
                onVacate();
              }
            }}
          >
            {t('council.vacate')}
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * Seats go to validated members (docs/07 §3.2), so the picker offers active
 * accounts and lets the api be the authority on eligibility — it answers
 * `holder_not_validated_member`, which reads back as a sentence.
 */
function GrantButton({
  seatNo,
  onGranted,
}: {
  seatNo: number;
  onGranted: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');

  const candidates = useQuery({
    queryKey: ['users', 'seat-candidates'],
    queryFn: () => usersApi.list({ status: 'active', has_person: 'true', limit: 100 }),
    enabled: open,
  });

  const grant = useMutation({
    mutationFn: () => councilApi.grant(selected, seatNo),
    onSuccess: async () => {
      setOpen(false);
      setSelected('');
      await onGranted();
    },
  });

  if (!open) {
    return (
      <button className={ui.buttonSecondary} type="button" onClick={() => setOpen(true)}>
        {t('council.grant')}
      </button>
    );
  }

  return (
    <div className={styles.grantRow}>
      <select
        className={ui.input}
        value={selected}
        aria-label={t('council.holder')}
        onChange={(event) => setSelected(event.target.value)}
      >
        <option value="">{t('council.choosePerson')}</option>
        {(candidates.data?.data ?? [])
          .filter((user) => user.person_status === 'validated' && !user.seat)
          .map((user) => (
            <option key={user.id} value={user.id}>
              {user.display_name}
            </option>
          ))}
      </select>
      <button
        className={ui.button}
        type="button"
        disabled={!selected || grant.isPending}
        onClick={() => grant.mutate()}
      >
        {grant.isPending ? t('app.saving') : t('council.grantConfirm')}
      </button>
      <button className={ui.buttonSecondary} type="button" onClick={() => setOpen(false)}>
        {t('app.cancel')}
      </button>
      {grant.isError && <p className={ui.problem}>{problemMessage(grant.error, t)}</p>}
    </div>
  );
}

/**
 * The settings editor of §3.11. The threshold slider is CHECK-mirrored
 * (`1..council_size`, migration 7's `settings_threshold_check`) so the form
 * cannot compose a value the database would reject, and the impact copy names
 * the consequence of the value currently selected.
 */
function SettingsEditor({
  view,
  activeSeats,
  onClose,
  onSaved,
}: {
  view: SettingsView;
  activeSeats: number;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [size, setSize] = useState(view.council_size);
  const [threshold, setThreshold] = useState(view.approval_threshold);
  const [windowDays, setWindowDays] = useState(view.voting_window_days);

  const save = useMutation({
    mutationFn: () =>
      settingsApi.update(
        {
          council_size: size,
          approval_threshold: Math.min(threshold, size),
          voting_window_days: windowDays,
        },
        view.updated_at,
      ),
    onSuccess: async () => {
      onClose();
      await onSaved();
    },
  });

  const effective = Math.min(threshold, activeSeats);

  return (
    <div
      className={styles.modalBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={t('council.edit')}
    >
      <div className={styles.modal}>
        <h2>{t('council.edit')}</h2>

        <label className={ui.field}>
          <span className={ui.label}>{t('admin.councilSize')}</span>
          <input
            className={ui.input}
            type="number"
            min={1}
            max={99}
            value={size}
            onChange={(event) => setSize(Number(event.target.value))}
          />
        </label>

        <label className={ui.field}>
          <span className={ui.label}>
            {t('admin.approvalThreshold')}:{' '}
            <span className="tabular">{Math.min(threshold, size)}</span>
          </span>
          <input
            type="range"
            min={1}
            max={Math.max(1, size)}
            value={Math.min(threshold, size)}
            onChange={(event) => setThreshold(Number(event.target.value))}
          />
        </label>

        <label className={ui.field}>
          <span className={ui.label}>{t('admin.votingWindow')}</span>
          <input
            className={ui.input}
            type="number"
            min={1}
            max={365}
            value={windowDays}
            onChange={(event) => setWindowDays(Number(event.target.value))}
          />
        </label>

        {/* Live impact copy — the ADR-0006 consequence, at the moment of choice. */}
        <p className={ui.notice} role="status">
          {Math.min(threshold, size) >= size
            ? t('council.impactUnanimity', { size })
            : t('council.impactMajority', { threshold: Math.min(threshold, size), size })}
        </p>
        {effective < Math.min(threshold, size) && (
          <p className={ui.muted}>
            {t('council.impactClamped', { effective, seats: activeSeats })}
          </p>
        )}
        {/* §5.2 rule 3: a threshold change re-tallies every open proposal at once. */}
        <p className={ui.muted}>{t('council.impactRetally')}</p>

        {save.isError && <p className={ui.problem}>{problemMessage(save.error, t)}</p>}

        <div className={styles.modalActions}>
          <button
            className={ui.button}
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('app.saving') : t('app.save')}
          </button>
          <button className={ui.buttonSecondary} type="button" onClick={onClose}>
            {t('app.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
}
